import * as Styled from './Button.styles';

import { FC, useMemo } from 'react';

import { ButtonProps, categoryType } from './types';
import { OUTLINED, PRIMARY, PUNCHUP_QUATERNARY, PUNCHUP_SECONDARY, PUNCHUP_TERTIARY, SECONDARY } from './constants';
import { useAppSelector } from 'store/hooks';
import { RootState } from 'store';

/* When the partner is on Punchup, the green theme variants are remapped to their
   orange Punchup equivalents so consumers don't have to change `category`. Variants
   without a Punchup counterpart (tertiary/red, quaternary/grey, underlined,
   already-punchup_*) pass through unchanged. */
const PUNCHUP_CATEGORY_MAP: Partial<Record<categoryType, categoryType>> = {
	[PRIMARY]: PUNCHUP_TERTIARY,
	[SECONDARY]: PUNCHUP_SECONDARY,
	[OUTLINED]: PUNCHUP_QUATERNARY,
};

const Button: FC<ButtonProps> = ({ text, category, type = 'button', refProp, ...rest }: ButtonProps) => {
	const { isPunchup } = useAppSelector(({ auth }: RootState) => auth);

	const ButtonComponent = useMemo(() => {
		const buttons = {
			primary: Styled.DefaultButton,
			secondary: Styled.SecondaryButton,
			tertiary: Styled.TertiaryButton,
			quaternary: Styled.QuaternaryButton,
			punchup_secondary: Styled.PunchupSecondaryButton,
			punchup_tertiary: Styled.PunchupTertiaryButton,
			punchup_quaternary: Styled.PunchupQuaternaryButton,
			outlined: Styled.OutlinedButton,
			underlined: Styled.UnderlinedButton,
		};

		const baseCategory = category ?? PRIMARY;
		const effectiveCategory = isPunchup ? PUNCHUP_CATEGORY_MAP[baseCategory] ?? baseCategory : baseCategory;
		return buttons[effectiveCategory];
	}, [category, isPunchup]);

	return (
		<ButtonComponent type={type} ref={refProp} {...rest}>
			{text}
		</ButtonComponent>
	);
};

export { Button };
